/**
 * Text-oriented PowerPoint tools: slide title, shape text, and text formatting.
 *
 * Handlers follow the PowerPoint.run/load/sync conventions documented in
 * pptx-tools.ts.
 */

import type { PptxTool } from "../tool-types";
import { resolveShape } from "../shape-builders";

export const textTools: PptxTool[] = [
  // ─── Set Slide Title ──────────────────────────────────────────────────────

  {
    schema: {
      name: "set_slide_title",
      description:
        "Set the title text of a slide. Finds the title placeholder shape and sets its text.",
      parameters: {
        type: "object",
        properties: {
          index: {
            type: "number",
            description: "0-based index of the slide.",
          },
          title: {
            type: "string",
            description: "The title text to set.",
          },
        },
        required: ["index", "title"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);
        const shapes = slide.shapes;
        shapes.load("items/name,items/type,items/placeholderFormat");

        await context.sync();

        // Find the title placeholder by checking placeholderFormat.type
        let titleShapeIndex = -1;
        for (let i = 0; i < shapes.items.length; i++) {
          const shape = shapes.items[i];
          try {
            shape.placeholderFormat.load("type");
          } catch {
            // shape has no placeholder format
          }
        }

        await context.sync();

        for (let i = 0; i < shapes.items.length; i++) {
          const shape = shapes.items[i];
          try {
            const pType = shape.placeholderFormat.type;
            if (
              pType === PowerPoint.PlaceholderType.title ||
              pType === PowerPoint.PlaceholderType.centerTitle
            ) {
              titleShapeIndex = i;
              break;
            }
          } catch {
            // no placeholder format
          }
        }

        if (titleShapeIndex === -1) {
          // Fallback: use the first shape
          if (shapes.items.length === 0) {
            throw new Error(`Slide ${args.index} has no shapes — it is a blank slide with no placeholders. Use add_text_box to add a title text box instead.`);
          }
          titleShapeIndex = 0;
        }

        shapes.items[titleShapeIndex].textFrame.textRange.text = args.title as string;

        await context.sync();

        return { success: true };
      });
    },
  },

  // ─── Set Shape Text ───────────────────────────────────────────────────────

  {
    schema: {
      name: "set_shape_text",
      description:
        "Set the text content of a shape on a slide, identified by its 0-based shape index.",
      parameters: {
        type: "object",
        properties: {
          slideIndex: {
            type: "number",
            description: "0-based index of the slide.",
          },
          shapeIndex: {
            type: "number",
            description: "0-based index of the shape within the slide.",
          },
          text: {
            type: "string",
            description: "Text to set on the shape.",
          },
        },
        required: ["slideIndex", "shapeIndex", "text"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.slideIndex as number);
        const shape = slide.shapes.getItemAt(args.shapeIndex as number);
        shape.textFrame.textRange.text = args.text as string;

        await context.sync();

        return { success: true };
      });
    },
  },

  // ─── Set Text Format ──────────────────────────────────────────────────────

  {
    // Font name, paragraph alignment, vertical alignment, and autofit are
    // PowerPointApi 1.4 surface.
    minRequirementSet: "1.4",
    schema: {
      name: "set_text_format",
      description:
        "Apply text formatting to the entire text of a shape: font size/name/color, bold, " +
        "italic, horizontal alignment (paragraph), vertical alignment (within the shape), and " +
        "autofit. Identify the shape by shapeId (preferred — use the id returned by get_slide / " +
        "add_text_box) OR by shapeIndex. All format properties are optional — supply only the " +
        "ones you want to change.",
      parameters: {
        type: "object",
        properties: {
          slideIndex: {
            type: "number",
            description: "0-based index of the slide.",
          },
          shapeId: {
            type: "string",
            description: "ID of the shape (from get_slide / add_text_box). Preferred over shapeIndex.",
          },
          shapeIndex: {
            type: "number",
            description: "0-based index of the shape on the slide. Used only when shapeId is not available.",
          },
          fontSize: {
            type: "number",
            description: "Font size in points, e.g. 72 for a hero number.",
          },
          fontName: {
            type: "string",
            description: 'Font family, e.g. "Segoe UI", "Calibri", "Aptos".',
          },
          color: {
            type: "string",
            description: "Font color as a hex string, e.g. '#38BDF8' or '#FFFFFF'.",
          },
          bold: {
            type: "boolean",
            description: "Whether the text should be bold.",
          },
          italic: {
            type: "boolean",
            description: "Whether the text should be italic.",
          },
          horizontalAlignment: {
            type: "string",
            description: 'Paragraph alignment: "left", "center", or "right".',
          },
          verticalAlignment: {
            type: "string",
            description: 'Vertical anchoring of text within the shape: "top", "middle", or "bottom".',
          },
          autofit: {
            type: "boolean",
            description:
              "true = shrink text to fit the shape on overflow; false = no auto-sizing. " +
              "Use true on fixed-zone layouts so long titles never spill.",
          },
        },
        required: ["slideIndex"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.slideIndex as number);

        const shapes = slide.shapes;
        shapes.load("items/id");
        await context.sync();
        const shape = resolveShape(shapes, args);

        const font = shape.textFrame.textRange.font;

        if (args.fontSize !== undefined) font.size = args.fontSize as number;
        if (args.fontName !== undefined) font.name = args.fontName as string;
        if (args.color   !== undefined) font.color = args.color as string;
        if (args.bold    !== undefined) font.bold = args.bold as boolean;
        if (args.italic  !== undefined) font.italic = args.italic as boolean;

        if (args.horizontalAlignment !== undefined) {
          const map: Record<string, string> = { left: "Left", center: "Center", right: "Right" };
          const mapped = map[String(args.horizontalAlignment)];
          if (!mapped) {
            throw new Error(
              `Unknown horizontalAlignment "${String(args.horizontalAlignment)}" — use left, center, or right.`
            );
          }
          shape.textFrame.textRange.paragraphFormat.horizontalAlignment =
            mapped as unknown as PowerPoint.ParagraphHorizontalAlignment;
        }

        if (args.verticalAlignment !== undefined) {
          const map: Record<string, string> = { top: "Top", middle: "Middle", bottom: "Bottom" };
          const mapped = map[String(args.verticalAlignment)];
          if (!mapped) {
            throw new Error(
              `Unknown verticalAlignment "${String(args.verticalAlignment)}" — use top, middle, or bottom.`
            );
          }
          shape.textFrame.verticalAlignment =
            mapped as unknown as PowerPoint.TextVerticalAlignment;
        }

        if (args.autofit !== undefined) {
          shape.textFrame.autoSizeSetting = ((args.autofit as boolean)
            ? "AutoSizeTextToFitShape"
            : "AutoSizeNone") as unknown as PowerPoint.ShapeAutoSize;
        }

        await context.sync();

        return { success: true };
      });
    },
  },
];
